import { useLingui } from '@lingui/react/macro';
import { LockKeyhole } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import type { Brand } from '@/shared/brand';

import { AuthFibers } from './auth-fibers';
import { AuthBrandBand, AuthStage, AuthTrustRow } from './auth-stage';

export interface AuthLayoutProps {
  /** Marca do domínio; ausente na falha do boot (sem config). */
  readonly brand?: Brand | undefined;
  /** `USE_MFA` do domínio: a prova de confiança cita o segundo fator só quando ele existe. */
  readonly mfa?: boolean;
  /** Muda a cada passo (o caminho): o conteúdo entra de novo, com a animação de subida. */
  readonly contentKey?: string;
  readonly children: ReactNode;
}

/**
 * Moldura de todas as telas de autenticação e da falha do boot: palco de marca à esquerda no desktop
 * (≥1024 px) e, abaixo disso, a faixa condensada em cima, com o formulário em primeiro plano.
 */
export function AuthLayout({ brand, mfa = false, contentKey, children }: AuthLayoutProps) {
  const { t } = useLingui();
  const name = brand?.name ?? 'UYZY';
  const year = new Date().getFullYear();
  // A aba diz o nome do produto: o título da marca (MAIN_TITLE) ganha "UYZY ·" enquanto o login está aberto.
  useEffect(() => {
    const previous = document.title;
    if (!/uyzy/i.test(previous)) document.title = previous ? t`UYZY · ${previous}` : 'UYZY';
    return () => {
      document.title = previous;
    };
  }, [t]);
  return (
    <div data-lume-theme="dark" className="uyzy-auth-root min-h-dvh bg-background text-foreground lg:grid lg:grid-cols-[minmax(0,1fr)_29rem] xl:grid-cols-[minmax(0,1fr)_32rem]">
      <AuthFibers />
      <AuthStage brand={brand} mfa={mfa} />
      <main className="uyzy-auth-panel relative flex min-h-dvh flex-col gap-6 px-4 pt-[max(env(safe-area-inset-top,0px),16px)] pb-[max(env(safe-area-inset-bottom,0px),20px)] sm:px-8 lg:z-10 lg:-ml-7 lg:rounded-l-[28px] lg:px-10 lg:py-10 xl:px-14">
        <AuthBrandBand brand={brand} />
        <div className="flex flex-1 items-start justify-center sm:items-center">
          <div key={contentKey} className="uyzy-rise w-full max-w-[25rem] py-2 sm:py-8">
            {children}
          </div>
        </div>
        <AuthTrustRow mfa={mfa}>
          <p className="inline-flex items-center gap-1.5">
            <LockKeyhole aria-hidden className="hidden size-3.5 lg:inline" />
            <span className="hidden lg:inline">{t`Ambiente seguro`} ·</span>
            <span>{t`© ${year} ${name}`}</span>
          </p>
        </AuthTrustRow>
      </main>
    </div>
  );
}
